/**
 * Hook para gerenciar dados de comissão
 * Centraliza chamadas à API e estado de tabelas de comissão
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export function useCommissionData() {
  // Estados principais
  const [bancos, setBancos] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [tabelasComissao, setTabelasComissao] = useState([]);
  const [tiposOperacao, setTiposOperacao] = useState([]);
  const [correspondentes, setCorrespondentes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Carregar todos os dados iniciais
  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const [bancosRes, instRes, tabelasRes, tiposRes, correspRes] = await Promise.all([
        axios.get(`${API}/api/bancos`),
        axios.get(`${API}/api/lookup/instituicoes`),
        axios.get(`${API}/api/tabelas-comissao`),
        axios.get(`${API}/api/tipos-operacao`),
        axios.get(`${API}/api/correspondentes`)
      ]);
      
      setBancos(bancosRes.data || []);
      setInstituicoes(instRes.data.instituicoes || instRes.data || []);
      setTabelasComissao(tabelasRes.data.tabelas || []);
      setTiposOperacao(tiposRes.data.tipos || []);
      setCorrespondentes(correspRes.data.correspondentes || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar dados ao montar
  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Recarregar tipos de operação
  const recarregarTiposOperacao = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/tipos-operacao`);
      setTiposOperacao(response.data.tipos || []);
    } catch (error) {
      console.error("Erro ao recarregar tipos:", error);
    }
  }, []);

  // Recarregar correspondentes
  const recarregarCorrespondentes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/correspondentes`);
      setCorrespondentes(response.data.correspondentes || []);
    } catch (error) {
      console.error("Erro ao recarregar correspondentes:", error);
    }
  }, []);

  // Criar nova tabela
  const criarTabela = useCallback(async (novaTabela) => {
    const nomeTabela = novaTabela.nome || novaTabela.tipo_operacao;
    
    if (!nomeTabela || !novaTabela.banco_id || !novaTabela.tipo_operacao) {
      toast.error("Preencha os campos obrigatórios (Banco e Tipo de Operação)");
      return false;
    }
    
    try {
      const response = await axios.post(`${API}/api/tabelas-comissao`, {
        nome: nomeTabela,
        banco_id: novaTabela.banco_id,
        instituicao: novaTabela.instituicao || "",
        tipo_operacao: novaTabela.tipo_operacao,
        correspondente: novaTabela.correspondente || "",
        prazos: novaTabela.prazos ? novaTabela.prazos.split(",").map(p => p.trim()).filter(p => p) : []
      });
      
      if (response.data) {
        toast.success("Tabela criada com sucesso!");
        await carregarDados();
        return true;
      }
    } catch (error) {
      console.error("Erro ao criar tabela:", error);
      toast.error("Erro ao criar tabela");
    }
    return false;
  }, [carregarDados]);

  // Excluir tabela
  const excluirTabela = useCallback(async (tabelaId) => {
    if (!window.confirm("Tem certeza que deseja excluir esta tabela?")) return false;
    
    try {
      await axios.delete(`${API}/api/tabelas-comissao/${tabelaId}`);
      toast.success("Tabela excluída");
      await carregarDados();
      return true;
    } catch (error) {
      console.error("Erro ao excluir tabela:", error);
      toast.error("Erro ao excluir tabela");
      return false;
    }
  }, [carregarDados]);

  // Salvar comissões de uma tabela
  const salvarComissoes = useCallback(async (tabelaId, comissoes) => {
    try {
      const tabela = tabelasComissao.find(t => t.id === tabelaId);
      if (!tabela) return false;
      
      await axios.put(`${API}/api/tabelas-comissao/${tabelaId}`, {
        ...tabela,
        comissoes: comissoes
      });
      
      toast.success("Comissões salvas!");
      
      // Atualizar dados localmente
      setTabelasComissao(prev => 
        prev.map(t => t.id === tabelaId ? { ...t, comissoes } : t)
      );
      return true;
    } catch (error) {
      console.error("Erro ao salvar comissões:", error);
      toast.error("Erro ao salvar comissões");
      return false;
    }
  }, [tabelasComissao]);

  // Adicionar tipo de operação
  const adicionarTipoOperacao = useCallback(async (novoTipo) => {
    if (!novoTipo.trim()) return false;
    if (tiposOperacao.includes(novoTipo.toUpperCase())) {
      toast.error("Este tipo já existe");
      return false;
    }
    
    try {
      await axios.post(`${API}/api/tipos-operacao`, {
        nome: novoTipo.toUpperCase()
      });
      toast.success("Tipo de operação adicionado");
      await recarregarTiposOperacao();
      return true;
    } catch (error) {
      console.error("Erro ao adicionar tipo:", error);
      toast.error(error.response?.data?.detail || "Erro ao adicionar tipo de operação");
      return false;
    }
  }, [tiposOperacao, recarregarTiposOperacao]);

  // Remover tipo de operação
  const removerTipoOperacao = useCallback(async (tipo) => {
    try {
      await axios.delete(`${API}/api/tipos-operacao/${encodeURIComponent(tipo)}`);
      toast.success("Tipo de operação removido");
      await recarregarTiposOperacao();
      return true;
    } catch (error) {
      console.error("Erro ao remover tipo:", error);
      toast.error(error.response?.data?.detail || "Erro ao remover tipo de operação");
      return false;
    }
  }, [recarregarTiposOperacao]);

  // Adicionar correspondente
  const adicionarCorrespondente = useCallback(async (novoCorrespondente) => {
    if (!novoCorrespondente.trim()) return false;
    if (correspondentes.includes(novoCorrespondente.toUpperCase())) {
      toast.error("Este correspondente já existe");
      return false;
    }
    
    try {
      await axios.post(`${API}/api/correspondentes`, {
        nome: novoCorrespondente.toUpperCase()
      });
      toast.success("Correspondente adicionado");
      await recarregarCorrespondentes();
      return true;
    } catch (error) {
      console.error("Erro ao adicionar correspondente:", error);
      toast.error(error.response?.data?.detail || "Erro ao adicionar correspondente");
      return false;
    }
  }, [correspondentes, recarregarCorrespondentes]);

  // Remover correspondente
  const removerCorrespondente = useCallback(async (corresp) => {
    try {
      await axios.delete(`${API}/api/correspondentes/${encodeURIComponent(corresp)}`);
      toast.success("Correspondente removido");
      await recarregarCorrespondentes();
      return true;
    } catch (error) {
      console.error("Erro ao remover correspondente:", error);
      toast.error(error.response?.data?.detail || "Erro ao remover correspondente");
      return false;
    }
  }, [recarregarCorrespondentes]);

  // Obter nome completo da instituição pelo código
  const getNomeInstituicao = useCallback((codigo) => {
    const instCompleta = instituicoes.find(inst => {
      const codigoInst = inst.split(" - ")[0];
      return codigoInst === codigo || inst === codigo;
    });
    return instCompleta || codigo;
  }, [instituicoes]);

  return {
    // Estados
    bancos,
    instituicoes,
    tabelasComissao,
    tiposOperacao,
    correspondentes,
    loading,
    // Ações
    carregarDados,
    criarTabela,
    excluirTabela,
    salvarComissoes,
    adicionarTipoOperacao,
    removerTipoOperacao,
    adicionarCorrespondente,
    removerCorrespondente,
    getNomeInstituicao
  };
}
